import { read, utils } from 'xlsx';
import type { CreateGlossaryTermRequest } from '../types';

export type ParsedGlossaryRow = {
  rowIndex: number;
  source_term: string;
  ru_term: string;
  attribute?: string;
  declension_policy: 'none' | 'wrapper_only' | 'name';
  notes?: string;
  normalizedKey: string;
};

export type GlossaryImportError = {
  rowIndex: number;
  message: string;
};

export type GlossaryImportConflict = {
  normalizedKey: string;
  rows: ParsedGlossaryRow[];
};

export type GlossaryImportParseResult = {
  items: CreateGlossaryTermRequest[];
  rows: ParsedGlossaryRow[];
  errors: GlossaryImportError[];
  conflicts: GlossaryImportConflict[];
};

function normalizeKey(input: string): string {
  return String(input ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function normalizeTextValue(input: unknown): string {
  return String(input ?? '').replace(/\s+/g, ' ').trim();
}

function normalizeHeader(input: string): string {
  return normalizeKey(input).replace(/[._-]+/g, ' ');
}

function pickCell(row: Record<string, unknown>, headers: string[]): string {
  for (const h of headers) {
    const v = row[h];
    const s = normalizeTextValue(v);
    if (s) return s;
  }
  return '';
}

function parseDeclensionPolicy(raw: string): 'none' | 'wrapper_only' | 'name' | null {
  const v = normalizeKey(raw);
  if (!v) return 'name';
  if (v === 'none') return 'none';
  if (v === 'wrapper_only') return 'wrapper_only';
  if (v === 'name') return 'name';
  return null;
}

function isEquivalent(a: ParsedGlossaryRow, b: ParsedGlossaryRow): boolean {
  return (
    a.normalizedKey === b.normalizedKey &&
    normalizeKey(a.ru_term) === normalizeKey(b.ru_term) &&
    normalizeKey(a.attribute ?? '') === normalizeKey(b.attribute ?? '') &&
    normalizeKey(a.notes ?? '') === normalizeKey(b.notes ?? '') &&
    a.declension_policy === b.declension_policy
  );
}

export async function importGlossaryFromXlsx(file: File): Promise<GlossaryImportParseResult> {
  const buf = await file.arrayBuffer();
  const workbook = read(buf, { type: 'array' });
  const sheetName =
    workbook.Sheets['Глоссарий'] ? 'Глоссарий' : workbook.SheetNames[0] ? workbook.SheetNames[0] : null;

  if (!sheetName) {
    return { items: [], rows: [], errors: [{ rowIndex: 0, message: 'Не найден лист в файле' }], conflicts: [] };
  }

  const sheet = workbook.Sheets[sheetName];
  const rawRows = utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

  const errors: GlossaryImportError[] = [];
  const byNormKey = new Map<string, ParsedGlossaryRow>();
  const conflictsMap = new Map<string, ParsedGlossaryRow[]>();
  const parsedRows: ParsedGlossaryRow[] = [];

  for (let i = 0; i < rawRows.length; i++) {
    const row = rawRows[i] ?? {};
    const normalizedRow: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(row)) {
      normalizedRow[normalizeHeader(k)] = v;
    }

    const sourceTerm = normalizeTextValue(
      pickCell(normalizedRow, [
        normalizeHeader('Оригинальное название'),
        normalizeHeader('Оригинал'),
        normalizeHeader('source_term'),
        normalizeHeader('original_term'),
      ])
    );
    const ruTerm = normalizeTextValue(
      pickCell(normalizedRow, [
        normalizeHeader('Русское название'),
        normalizeHeader('Перевод'),
        normalizeHeader('ru_term'),
        normalizeHeader('translated_term'),
      ])
    );
    const attribute = normalizeTextValue(
      pickCell(normalizedRow, [normalizeHeader('Пол/Атрибут'), normalizeHeader('Атрибут'), normalizeHeader('type')])
    );
    const notes = normalizeTextValue(
      pickCell(normalizedRow, [normalizeHeader('Описание'), normalizeHeader('notes'), normalizeHeader('description')])
    );
    const declensionRaw = normalizeTextValue(
      pickCell(normalizedRow, [normalizeHeader('Правило склонения'), normalizeHeader('declension_policy')])
    );
    const declensionPolicy = parseDeclensionPolicy(declensionRaw);

    const rowIndex = i + 2;
    if (!sourceTerm) {
      errors.push({ rowIndex, message: 'Пустое значение в колонке "Оригинальное название"' });
      continue;
    }
    if (!ruTerm) {
      errors.push({ rowIndex, message: 'Пустое значение в колонке "Русское название"' });
      continue;
    }
    if (!declensionPolicy) {
      errors.push({
        rowIndex,
        message: 'Некорректное значение в колонке "Правило склонения" (ожидалось none/wrapper_only/name)',
      });
      continue;
    }

    const normalizedKey = normalizeKey(sourceTerm);
    const parsed: ParsedGlossaryRow = {
      rowIndex,
      source_term: normalizeTextValue(sourceTerm),
      ru_term: normalizeTextValue(ruTerm),
      attribute: attribute || undefined,
      declension_policy: declensionPolicy,
      notes: notes || undefined,
      normalizedKey,
    };
    parsedRows.push(parsed);

    const prev = byNormKey.get(normalizedKey);
    if (!prev) {
      byNormKey.set(normalizedKey, parsed);
      continue;
    }

    if (isEquivalent(prev, parsed)) {
      continue;
    }

    const list = conflictsMap.get(normalizedKey) ?? [prev];
    list.push(parsed);
    conflictsMap.set(normalizedKey, list);
  }

  const conflicts: GlossaryImportConflict[] = Array.from(conflictsMap.entries()).map(([normalizedKey, rows]) => ({
    normalizedKey,
    rows,
  }));

  for (const normalizedKey of conflictsMap.keys()) {
    byNormKey.delete(normalizedKey);
  }

  const items: CreateGlossaryTermRequest[] = Array.from(byNormKey.values()).map((r) => ({
    source_term: r.source_term,
    ru_term: r.ru_term,
    attribute: r.attribute,
    declension_policy: r.declension_policy,
    notes: r.notes,
  }));

  return { items, rows: parsedRows, errors, conflicts };
}

